import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Target,
  BarChart3,
  Building2,
  HardHat,
  AlertTriangle,
  HeartPulse,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO45001_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#2d1b4e] to-[#4a1a6b] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-400 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-violet-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <HardHat size={18} className="text-purple-300" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO 45001 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Occupational Health & Safety Management System (OHSMS) — The
            internationally recognized standard for ensuring safe, healthy
            workplaces and proactive risk management.
          </p>
        </motion.div>
      </section>

      {/* Overview Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO 45001 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO 45001 is an internationally recognized standard for
              Occupational Health and Safety Management Systems (OHSMS). It
              provides a structured framework for organizations to identify,
              control, and reduce occupational health and safety risks, ensuring
              a safe and healthy workplace for employees and other stakeholders.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              The standard applies to organizations of all sizes and sectors and
              supports a proactive approach to accident prevention, hazard
              control, legal compliance, and continual improvement in workplace
              health and safety performance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is ISO 45001 */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO 45001 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO 45001 certification confirms that an organization has
              implemented an effective Occupational Health and Safety Management
              System (OHSMS) to manage health and safety risks systematically.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              The certification focuses on:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  icon: AlertTriangle,
                  text: "Hazard identification and risk assessment",
                },
                {
                  icon: ShieldCheck,
                  text: "Elimination of hazards and reduction of OH&S risks",
                },
                {
                  icon: ClipboardList,
                  text: "Legal and regulatory compliance",
                },
                { icon: Users, text: "Worker participation and consultation" },
                {
                  icon: TrendingUp,
                  text: "Emergency preparedness and continual improvement",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <item.icon
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-base mt-6 italic">
              ISO 45001 replaces OHSAS 18001 and aligns health and safety
              management with modern risk-based thinking.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO 45001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: HardHat,
                title: "Workplace Safety Assurance",
                desc: "Protects workers by reducing injuries, illnesses, and work-related incidents.",
              },
              {
                icon: AlertTriangle,
                title: "Risk-Based Approach",
                desc: "Identifies hazards proactively and implements preventive controls.",
              },
              {
                icon: ShieldCheck,
                title: "Legal & Regulatory Compliance",
                desc: "Supports compliance with occupational health and safety laws.",
              },
              {
                icon: Users,
                title: "Worker Participation",
                desc: "Encourages employee involvement in health and safety decision-making.",
              },
              {
                icon: Globe,
                title: "Integration with Other ISO Standards",
                desc: "Easily integrates with ISO 9001 and ISO 14001.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What is OHSMS */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is an Occupational Health & Safety Management System (OHSMS)?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              An Occupational Health and Safety Management System is a
              structured framework that enables organizations to:
            </p>
            <div className="space-y-3 mb-6">
              {[
                "Identify workplace hazards and assess risks",
                "Implement controls and safe work procedures",
                "Monitor incidents, near misses, and unsafe conditions",
                "Improve safety culture and employee awareness",
                "Continually improve OH&S performance",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mt-0.5 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-lg leading-relaxed">
              An effective OHSMS embeds safety into daily operations and
              organizational culture.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Importance for Businesses */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Importance of ISO 45001 Certification for Businesses
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Reduces workplace accidents and occupational diseases",
                "Improves compliance with health and safety legislation",
                "Enhances employee morale, trust, and productivity",
                "Reduces downtime, compensation claims, and insurance costs",
                "Strengthens corporate reputation and stakeholder confidence",
                "Demonstrates leadership commitment to worker safety",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* OHSMS Certification */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is OHSMS Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO 45001 certification is granted by an accredited certification
              body after a comprehensive audit of:
            </p>
            <div className="space-y-3 mb-6">
              {[
                "OHSMS documentation and policies",
                "Hazard identification and risk assessments",
                "Operational controls and emergency preparedness",
                "Incident investigation and corrective actions",
                "Training, internal audits, and management reviews",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <FileCheck
                    size={20}
                    className="text-[var(--bg-primary)] mt-0.5 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-lg leading-relaxed">
              Certification confirms that the organization complies with ISO
              45001 requirements and effectively manages OH&S risks.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Significance of ISO 45001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-4">
              ISO 45001 helps organizations:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Demonstrate commitment to worker health and safety",
                "Prevent injuries, illnesses, and fatalities",
                "Improve safety performance and accountability",
                "Enhance legal compliance and risk control",
                "Build a strong safety culture across all levels",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <CheckCircle
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Advantages of ISO 45001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Safer and healthier workplace",
                "Reduced incidents and operational disruptions",
                "Improved employee engagement and retention",
                "Enhanced compliance and audit readiness",
                "Stronger market reputation and tender eligibility",
                "Continuous improvement in health and safety performance",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Industries That Commonly Require ISO 45001
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO 45001 is widely adopted in high-risk and compliance-driven
              sectors, including:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Building2, name: "Construction & Engineering" },
                { icon: Factory, name: "Manufacturing" },
                { icon: Factory, name: "Oil & Gas" },
                { icon: Factory, name: "Mining & Minerals" },
                { icon: HeartPulse, name: "Chemical & Pharmaceutical" },
                { icon: Globe, name: "Logistics & Transportation" },
                { icon: Building2, name: "Utilities and Public Sector" },
                { icon: Building2, name: "Facilities Management" },
              ].map((industry, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-center gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <industry.icon
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">
                    {industry.name}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Applications of ISO 45001 in Industry
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Hazard identification and risk assessment (HIRA)",
                "Safe operating procedures and permit systems",
                "PPE management and safety training",
                "Incident reporting and investigation",
                "Emergency preparedness and response planning",
                "Contractor and visitor safety management",
              ].map((app, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100 shadow-sm"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{app}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Get Certified */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Get ISO 45001 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              The certification process typically includes:
            </p>
            <div className="space-y-4">
              {[
                "Conducting an OH&S Gap Analysis",
                "Identifying hazards and assessing risks",
                "Establishing legal and regulatory registers",
                "Developing OHSMS documentation and controls",
                "Training workers and supervisors",
                "Conducting internal audits and management reviews",
                "Certification audit by an accredited certification body",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#2d1b4e] to-[#4a1a6b] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO 45001 Certification?
            </h2>
            <div className="w-20 h-1 bg-purple-400 rounded-full mx-auto mb-6" />
            <p className="text-gray-200 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. provides comprehensive ISO 45001
              consultancy services, helping organizations build safe, compliant,
              and resilient workplaces.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-200 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Hazard identification and risk assessment",
                "OHSMS documentation and implementation",
                "Legal compliance and safety audits",
                "Internal and mock audits",
                "Certification body coordination and audit support",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-200 text-base leading-relaxed max-w-3xl mx-auto">
              We help organizations achieve safer operations, regulatory
              compliance, and sustainable workforce protection through practical
              ISO 45001 implementation.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO45001_Details;
